<script setup lang="ts">
import { computed, type ModelRef, watch, onUnmounted } from 'vue'
import { default as PButton } from 'primevue/button'
import { default as PTextarea } from 'primevue/textarea'
import { default as PMessage } from 'primevue/message'
import CQuizQuestionAnswerSingleChoice from '@/presentation/components/quiz/CQuizQuestionAnswerSingleChoice.vue'
import CQuizQuestionAnswerMultipleChoice from '@/presentation/components/quiz/CQuizQuestionAnswerMultipleChoice.vue'
import CQuizQuestionAnswerTrueFalse from '@/presentation/components/quiz/CQuizQuestionAnswerTrueFalse.vue'
import CQuizQuestionAnswerSequencing from '@/presentation/components/quiz/CQuizQuestionAnswerSequencing.vue'
import CQuizQuestionAnswerMatching from '@/presentation/components/quiz/CQuizQuestionAnswerMatching.vue'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import type { QuizQuestion } from '@/core/domain/quiz/QuizQuestion.ts'

const questionModel: ModelRef<QuizQuestion> = defineModel('question', { required: true })

const props = defineProps<{
  validateUpdate: () => void
  canModifyStructure?: boolean
  canModifyContent?: boolean
  quizType?: QuizType.Values
}>()

const emit = defineEmits<{
  cancel: []
  'question-updated': [question: QuizQuestion]
}>()

// Débounce l'émission pour éviter les mises à jour trop fréquentes
let updateTimeout: NodeJS.Timeout | null = null

watch(
  questionModel,
  (newQuestion) => {
    if (newQuestion) {
      // Annuler le précédent timeout
      if (updateTimeout) {
        clearTimeout(updateTimeout)
      }

      // Programmer la mise à jour avec un délai
      updateTimeout = setTimeout(() => {
        emit('question-updated', newQuestion)
      }, 300) // 300ms de délai
    }
  },
  { deep: true }
)

// Nettoyer le timeout quand le composant est démonté
onUnmounted(() => {
  if (updateTimeout) {
    clearTimeout(updateTimeout)
  }
})

const componentAnswer = computed(() => {
  switch (questionModel.value.type) {
    case LearningQuizQuestionType.Values.SINGLE_CHOICE:
      return CQuizQuestionAnswerSingleChoice
    case LearningQuizQuestionType.Values.MULTIPLE_CHOICE:
      return CQuizQuestionAnswerMultipleChoice
    case LearningQuizQuestionType.Values.TRUE_FALSE:
      return CQuizQuestionAnswerTrueFalse
    case LearningQuizQuestionType.Values.SEQUENCING:
      return CQuizQuestionAnswerSequencing
    case LearningQuizQuestionType.Values.MATCHING:
      return CQuizQuestionAnswerMatching
    default:
      return CQuizQuestionAnswerSingleChoice
  }
})

const isQuestionValid = computed(() => {
  return questionModel.value.isValid()
})

const validationErrors = computed(() => {
  return questionModel.value.getValidationErrors()
})

const isDiagnosticQuiz = computed(() => {
  return (
    props.quizType === QuizType.Values.DIAG_START || props.quizType === QuizType.Values.DIAG_END
  )
})

function handleValidateUpdate() {
  if (isQuestionValid.value) {
    props.validateUpdate()
  }
}
</script>

<template>
  <div class="grid grid-cols-12 gap-4">
    <div class="col-span-12 flex items-center justify-between">
      <h4 class="text-color block font-bold">Paramètre de la question {{ questionModel.order }}</h4>

      <div class="flex items-center gap-2">
        <p-button severity="secondary" label="Annuler" @click="$emit('cancel')" />
        <p-button
          label="Valider"
          class="ml-4"
          @click="handleValidateUpdate()"
          :disabled="!isQuestionValid"
        />
        <p-message v-if="!isQuestionValid" severity="warn" class="text-xs">
          Corrigez les erreurs pour valider
        </p-message>
      </div>
    </div>

    <div class="col-span-12 mt-8">
      <h5 class="font-bold">Réponses</h5>
      <p-message severity="info" v-if="questionModel.answers.length === 0">
        Il n'y pas encore de réponses pour cette question. Cliquer sur le bouton pour ajouter une
        réponse.
      </p-message>

      <!-- Affichage des erreurs de validation -->
      <div v-if="validationErrors.length > 0" class="mb-4">
        <p-message
          v-for="error in validationErrors"
          :key="error"
          severity="error"
          class="mb-2 block"
        >
          {{ error }}
        </p-message>
      </div>

      <component
        :is="componentAnswer"
        v-model:answers="questionModel.answers"
        :can-modify-structure="props.canModifyStructure"
        :can-modify-content="props.canModifyContent"
      />
    </div>

    <div v-if="!isDiagnosticQuiz" class="col-span-12 mt-8">
      <h5 class="font-bold">Feedbacks</h5>

      <div class="flex flex-col gap-2">
        <p>Réponse juste</p>
        <p-textarea class="w-full" v-model="questionModel.feedbacks.correct" />
      </div>

      <div class="mt-4 flex flex-col gap-2">
        <p>Réponse fausse</p>
        <p-textarea class="w-full" v-model="questionModel.feedbacks.wrong" />
      </div>
    </div>
  </div>
</template>
